import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import "./AdminDashboard.css";
import AdminWorldMap from "../components/admin/AdminWorldMap";
import AdminTrafficSources from "../components/admin/AdminTrafficSources";
import { buildApiUrl, buildPublicFileUrl } from "../config/api";

const COLORS = ["#f0bf16", "#2d9cdb", "#74c476", "#ff8a3d", "#9b59b6"];

const emptyChartData = [
  { name: "Mon", value: 0 },
  { name: "Tue", value: 0 },
  { name: "Wed", value: 0 },
  { name: "Thu", value: 0 },
  { name: "Fri", value: 0 },
  { name: "Sat", value: 0 },
  { name: "Sun", value: 0 },
];

function getValue(item, keys, fallback = "") {
  for (const key of keys) {
    if (
      item?.[key] !== undefined &&
      item?.[key] !== null &&
      item?.[key] !== ""
    ) {
      return item[key];
    }
  }

  return fallback;
}

function toNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function formatMoney(value) {
  return `$${toNumber(value, 0).toFixed(2)}`;
}

function formatNumber(value) {
  return new Intl.NumberFormat("en-US").format(toNumber(value, 0));
}

function getImagePath(product) {
  return getValue(
    product,
    [
      "image",
      "cover_image",
      "coverImage",
      "thumbnail",
      "thumbnail_url",
      "image_url",
      "preview_image",
      "main_image",
    ],
    ""
  );
}

function getImageSrc(path) {
  if (!path) return "";

  if (
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("blob:") ||
    path.startsWith("data:")
  ) {
    return path;
  }

  if (path.startsWith("/") || path.startsWith("uploads/")) {
    return buildPublicFileUrl(path);
  }

  return path;
}

function makeProductTitle(product) {
  return getValue(product, ["title", "name", "product_title"], "Untitled Product");
}

function makePrice(product) {
  return toNumber(
    getValue(product, ["price", "personal_price", "personalPrice"], 0),
    0
  );
}

function getProductViews(product) {
  return toNumber(
    getValue(product, ["views", "view_count", "viewCount", "total_views"], 0),
    0
  );
}

function getVisitorDate(visitor) {
  return getValue(
    visitor,
    ["created_at", "createdAt", "timestamp", "visited_at", "date"],
    ""
  );
}

async function fetchJson(url) {
  const res = await fetch(url, {
    headers: { Accept: "application/json" },
    credentials: "include",
  });

  const text = await res.text();

  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error(`Invalid JSON response from ${url}`);
  }

  if (!res.ok) {
    throw new Error(data.message || `Request failed: ${url}`);
  }

  return data;
}

function EmptyBox({ text = "No data yet" }) {
  return <div className="admin-empty-box">{text}</div>;
}

function AdminDashboard() {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [orders, setOrders] = useState([]);
  const [newsletterStats, setNewsletterStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await fetch(buildApiUrl("/admin/auth/logout"), {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
        },
      });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      navigate("/admin-login", { replace: true });
    }
  };

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [productsResult, visitorsResult, ordersResult, newsletterResult] =
        await Promise.allSettled([
          fetchJson(buildApiUrl("/products")),
          fetchJson(buildApiUrl("/analytics/stats")),
          fetchJson(buildApiUrl("/admin/orders")),
          fetchJson(buildApiUrl("/admin/newsletter/stats")),
        ]);

      if (productsResult.status === "fulfilled") {
        const data = productsResult.value;
        setProducts(Array.isArray(data) ? data : data.products || data.data || []);
      }

      if (visitorsResult.status === "fulfilled") {
        const data = visitorsResult.value;

        if (Array.isArray(data)) {
          setVisitors(data);
        } else {
          setVisitors(
            data.visitors ||
              data.data ||
              data.recentVisitors ||
              data.recent_visitors ||
              []
          );
        }
      }

      if (ordersResult.status === "fulfilled") {
        const data = ordersResult.value;
        setOrders(Array.isArray(data) ? data : data.orders || data.data || []);
      }

      if (newsletterResult.status === "fulfilled") {
        const data = newsletterResult.value;
        setNewsletterStats(data.summary || data || {});
      }
    } catch (err) {
      setError(err.message || "Dashboard data could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const closeOnEscape = (event) => event.key === "Escape" && setMenuOpen(false);
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [menuOpen]);

  const liveProducts = useMemo(() => {
    return products.filter((item) => {
      const status = String(item.status || "").toLowerCase();
      return status !== "deleted";
    });
  }, [products]);

  const stats = useMemo(() => {
    const publishedProducts = liveProducts.filter((item) => {
      const status = String(item.status || "").toLowerCase();
      return status === "published" || item.is_published === 1;
    });

    const totalSales = orders.reduce((sum, order) => {
      return sum + toNumber(getValue(order, ["total", "amount", "total_amount"], 0));
    }, 0);

    const productViews = liveProducts.reduce((sum, product) => {
      return sum + getProductViews(product);
    }, 0);

    return {
      totalVisitors: visitors.length,
      productViews,
      totalSales,
      orders: orders.length,
      totalProducts: liveProducts.length,
      publishedProducts: publishedProducts.length,
      unpublishedProducts: liveProducts.length - publishedProducts.length,
      newsletterSubscribers: toNumber(
        newsletterStats.total_subscribers ||
          newsletterStats.totalSubscribers ||
          newsletterStats.active_subscribers ||
          newsletterStats.activeSubscribers,
        0
      ),
    };
  }, [liveProducts, visitors, orders, newsletterStats]);

  const productStatusData = useMemo(
    () => [
      { name: "Published", value: stats.publishedProducts },
      { name: "Unpublished", value: stats.unpublishedProducts },
    ],
    [stats.publishedProducts, stats.unpublishedProducts]
  );

  const reportDate = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date());

  const exportReport = () => {
    const rows = [
      ["Metric", "Value"],
      ["Visitors", stats.totalVisitors],
      ["Product views", stats.productViews],
      ["Total sales", stats.totalSales.toFixed(2)],
      ["Orders", stats.orders],
      ["Products", stats.totalProducts],
      ["Published products", stats.publishedProducts],
      ["Unpublished products", stats.unpublishedProducts],
      ["Newsletter subscribers", stats.newsletterSubscribers],
    ];
    const csv = rows.map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `mayachitro-admin-report-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const deviceData = useMemo(() => {
    const grouped = visitors.reduce((acc, item) => {
      const device = getValue(
        item,
        ["device_type", "device", "platform", "deviceType"],
        "Unknown"
      );

      acc[device] = (acc[device] || 0) + 1;
      return acc;
    }, {});

    const result = Object.entries(grouped).map(([name, value]) => ({
      name,
      value,
    }));

    return result.length ? result : [{ name: "No data", value: 1 }];
  }, [visitors]);

  const visitorChartData = useMemo(() => {
    if (!visitors.length) return emptyChartData;

    const lastSeven = [...visitors].slice(-7);

    return lastSeven.map((visitor, index) => {
      const dateValue = getVisitorDate(visitor);
      const label = dateValue
        ? new Date(dateValue).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          })
        : `Visit ${index + 1}`;

      return {
        name: label,
        value: 1,
      };
    });
  }, [visitors]);

  const latestProducts = useMemo(() => {
    return [...liveProducts]
      .sort((a, b) => toNumber(b.id) - toNumber(a.id))
      .slice(0, 5);
  }, [liveProducts]);

  const topProducts = useMemo(() => {
    return [...liveProducts]
      .sort((a, b) => getProductViews(b) - getProductViews(a))
      .slice(0, 5);
  }, [liveProducts]);

  return (
    <div className="mc-admin-layout">
      <aside
        className={`mc-admin-sidebar ${menuOpen ? "open" : ""}`}
        onClick={(event) => event.target.closest("a, button") && setMenuOpen(false)}
      >
        <div className="mc-admin-brand">
          <div className="mc-admin-logo">MayaChitro</div>
          <div className="mc-admin-subtitle">Admin Panel</div>
        </div>

        <nav className="mc-admin-nav">
          <Link className="active" to="/admin">
            Dashboard
          </Link>
          <Link to="/admin/products">Photos / Products</Link>
          <Link to="/admin/image-optimizer">Image Optimizer</Link>
          <Link to="/admin/collections">Collections</Link>
          <Link to="/admin/orders">Orders & Sales</Link>
          <Link to="/admin/customers">Customers</Link>
          <Link to="/admin/downloads">Downloads</Link>
          <Link to="/admin/photo-stories">Photo Stories</Link>
          <Link to="/admin/vlog-posts">Vlog Posts</Link>
          <Link to="/admin/licensing">Licensing</Link>
          <Link to="/admin/coupons">Coupons</Link>
          <Link to="/admin/analytics">Visitor Analytics</Link>
          <Link to="/admin/messages">Messages / Inquiries</Link>
          <Link to="/admin/hero-slides">Hero Slides</Link>
          <Link to="/admin/newsletter">Newsletter Subscribers</Link>

          <div className="mc-admin-nav-divider" />

          <Link to="/admin/settings">Settings</Link>
          <Link to="/admin/users">Admin Users</Link>
          <Link to="/admin/activity-logs">Activity Logs</Link>

          <button
            type="button"
            className="mc-admin-nav-logout"
            onClick={handleLogout}
          >
            Logout
          </button>
        </nav>

        <div className="mc-admin-user">
          <div className="mc-admin-avatar">MC</div>
          <div>
            <strong>MayaChitro Admin</strong>
            <span>Owner</span>
          </div>
        </div>
      </aside>

      {menuOpen && <button type="button" className="mc-admin-menu-backdrop" aria-label="Close menu" onClick={() => setMenuOpen(false)} />}

      <main className="mc-admin-main">
        <header className="mc-admin-topbar">
          <button className="mc-menu-btn" type="button" aria-expanded={menuOpen} aria-label="Toggle admin menu" onClick={() => setMenuOpen((open) => !open)}>
            Menu
          </button>

          <div className="mc-topbar-actions">
            <button
              type="button"
              className="mc-topbar-logout"
              onClick={handleLogout}
            >
              Logout
            </button>
          </div>
        </header>

        <section className="mc-admin-page-head">
          <div>
            <h1>Dashboard</h1>
            <p>
              Product summary, visitor activity, newsletter growth, and quick
              controls.
            </p>
          </div>

          <div className="mc-admin-head-actions">
            <span className="mc-date-btn">Updated {reportDate}</span>
            <button type="button" className="mc-dark-btn" onClick={loadDashboard}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
            <button type="button" className="mc-gold-btn" onClick={exportReport}>
              Export Report
            </button>
          </div>
        </section>

        {error && <div className="mc-admin-error">{error}</div>}

        <section className="mc-admin-stats-grid">
          <div className="mc-stat-card">
            <div className="mc-stat-icon">👥</div>
            <div>
              <span>Total Visitors</span>
              <strong>{formatNumber(stats.totalVisitors)}</strong>
              <small>Tracked website visits</small>
            </div>
          </div>

          <div className="mc-stat-card">
            <div className="mc-stat-icon">👁</div>
            <div>
              <span>Photo Views</span>
              <strong>{formatNumber(stats.productViews)}</strong>
              <small>Based on product detail views</small>
            </div>
          </div>

          <div className="mc-stat-card">
            <div className="mc-stat-icon">💰</div>
            <div>
              <span>Total Sales</span>
              <strong>{formatMoney(stats.totalSales)}</strong>
              <small>Stripe connection later</small>
            </div>
          </div>

          <div className="mc-stat-card">
            <div className="mc-stat-icon">🛒</div>
            <div>
              <span>Orders</span>
              <strong>{formatNumber(stats.orders)}</strong>
              <small>Completed purchases</small>
            </div>
          </div>
        </section>

        <section className="mc-admin-stats-grid product-row">
          <div className="mc-stat-card small">
            <span>Total Products</span>
            <strong>{stats.totalProducts}</strong>
            <small>{stats.publishedProducts} published</small>
          </div>

          <div className="mc-stat-card small">
            <span>Published Photos</span>
            <strong>{stats.publishedProducts}</strong>
            <small>Photography downloads</small>
          </div>

          <div className="mc-stat-card small">
            <span>Unpublished Photos</span>
            <strong>{stats.unpublishedProducts}</strong>
            <small>Draft and hidden products</small>
          </div>

          <div className="mc-stat-card small">
            <span>Newsletter Subscribers</span>
            <strong>{stats.newsletterSubscribers}</strong>
            <small>Email audience</small>
          </div>
        </section>

        <section className="mc-dashboard-grid">
          <div className="mc-panel mc-sales-panel">
            <div className="mc-panel-head">
              <div>
                <h2>Visitor Overview</h2>
                <p>{formatNumber(stats.totalVisitors)} total visits</p>
              </div>
              <button type="button">This Week</button>
            </div>

            <ResponsiveContainer width="100%" height={210}>
              <AreaChart data={visitorChartData}>
                <XAxis dataKey="name" stroke="#7d8788" />
                <YAxis allowDecimals={false} stroke="#7d8788" />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#f0bf16"
                  fill="#f0bf16"
                  fillOpacity={0.22}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="mc-panel">
            <div className="mc-panel-head">
              <h2>Photo Status</h2>
              <button type="button">Live</button>
            </div>

            <div className="mc-donut-wrap">
              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie
                    data={productStatusData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={54}
                    outerRadius={78}
                  >
                    {productStatusData.map((entry, index) => (
                      <Cell
                        key={entry.name}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>

              <div className="mc-chart-legend">
                <span>
                  <b className="dot gold" /> Published{" "}
                  {stats.publishedProducts}
                </span>
                <span>
                  <b className="dot blue" /> Unpublished{" "}
                  {stats.unpublishedProducts}
                </span>
              </div>
            </div>
          </div>

          <div className="mc-panel mc-top-products">
            <div className="mc-panel-head">
              <h2>Top Products</h2>
              <Link to="/admin/products">View all</Link>
            </div>

            <div className="mc-product-table">
              <div className="mc-product-row header">
                <span>Product</span>
                <span>Views</span>
                <span>Revenue</span>
              </div>

              {topProducts.length ? (
                topProducts.map((product) => {
                  const image = getImageSrc(getImagePath(product));
                  const views = getProductViews(product);

                  return (
                    <div
                      className="mc-product-row"
                      key={product.id || product.slug}
                    >
                      <div className="mc-product-mini">
                        {image ? (
                          <img src={image} alt={makeProductTitle(product)} />
                        ) : null}
                        <div>
                          <strong>{makeProductTitle(product)}</strong>
                          <small>Digital Photo</small>
                        </div>
                      </div>
                      <span>{views}</span>
                      <span>{formatMoney(makePrice(product))}</span>
                    </div>
                  );
                })
              ) : (
                <EmptyBox text="No product data yet." />
              )}
            </div>
          </div>

          <div className="mc-panel mc-world-panel">
            <div className="mc-panel-head">
              <h2>Visitor World Map</h2>
              <button type="button">Live</button>
            </div>

            <AdminWorldMap visitors={visitors} />
          </div>

          <div className="mc-panel mc-traffic-panel">
            <div className="mc-panel-head">
              <h2>Traffic Sources</h2>
              <button type="button">Live</button>
            </div>

            <AdminTrafficSources visitors={visitors} />
          </div>

          <div className="mc-panel">
            <div className="mc-panel-head">
              <h2>Recent Orders</h2>
              <Link to="/admin/orders">View all</Link>
            </div>

            {orders.length ? (
              <div className="mc-simple-list">
                {orders.slice(0, 5).map((order) => (
                  <div className="mc-simple-item" key={order.id}>
                    <span>
                      {getValue(order, ["customer_name", "customer"], "Customer")}
                    </span>
                    <strong>
                      {formatMoney(getValue(order, ["total", "amount"], 0))}
                    </strong>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyBox text="No orders yet. Stripe order system will fill this later." />
            )}
          </div>

          <div className="mc-panel">
            <div className="mc-panel-head">
              <h2>Visitors by Device</h2>
              <button type="button">Live</button>
            </div>

            <div className="mc-donut-wrap">
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={deviceData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={50}
                    outerRadius={72}
                  >
                    {deviceData.map((entry, index) => (
                      <Cell
                        key={entry.name}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>

              <div className="mc-chart-legend">
                {deviceData.map((item, index) => (
                  <span key={item.name}>
                    <b
                      className="dot"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />{" "}
                    {item.name}: {item.value}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="mc-panel">
            <div className="mc-panel-head">
              <h2>Quick Actions</h2>
            </div>

            <div className="mc-quick-grid">
              <Link to="/admin/products">Upload Photos / Products</Link>
              <Link to="/admin/collections">Manage Collections</Link>
              <Link to="/admin/image-optimizer">Optimize Product Images</Link>
              <Link to="/admin/hero-slides">Manage Hero Slides</Link>
              <Link to="/admin/orders">Manage Orders</Link>
              <Link to="/admin/customers">Manage Customers</Link>
              <Link to="/admin/newsletter">Manage Newsletter</Link>
            </div>
          </div>

          <div className="mc-panel">
            <div className="mc-panel-head">
              <h2>Platform Overview</h2>
            </div>

            <div className="mc-overview-list">
              <Link to="/admin/products">
                <span>Total Photos / Products</span>
                <strong>{stats.totalProducts}</strong>
              </Link>
              <Link to="/admin/products">
                <span>Published Photos</span>
                <strong>{stats.publishedProducts}</strong>
              </Link>
              <Link to="/admin/orders">
                <span>Orders</span>
                <strong>{stats.orders}</strong>
              </Link>
              <Link to="/admin/customers">
                <span>Customer Management</span>
                <strong>Open</strong>
              </Link>
              <Link to="/admin/newsletter">
                <span>Newsletter Subscribers</span>
                <strong>{stats.newsletterSubscribers}</strong>
              </Link>
            </div>
          </div>

          <div className="mc-panel">
            <div className="mc-panel-head">
              <h2>Latest Products</h2>
              <Link to="/admin/products">View all</Link>
            </div>

            <div className="mc-latest-products">
              {latestProducts.length ? (
                latestProducts.map((product) => {
                  const image = getImageSrc(getImagePath(product));

                  return (
                    <div
                      className="mc-latest-product"
                      key={product.id || product.slug}
                    >
                      {image ? (
                        <img src={image} alt={makeProductTitle(product)} />
                      ) : null}
                      <div>
                        <strong>{makeProductTitle(product)}</strong>
                        <span>MayaChitro</span>
                      </div>
                      <small>{formatMoney(makePrice(product))}</small>
                    </div>
                  );
                })
              ) : (
                <EmptyBox text="No latest products found." />
              )}
            </div>
          </div>
        </section>

        <footer className="mc-admin-footer">
          <span>© 2026 MayaChitro Admin Panel. All rights reserved.</span>
          <span>Original photography by MayaChitro</span>
        </footer>
      </main>
    </div>
  );
}

export default AdminDashboard;
